// src/components/Combobox/Combobox.tsx
import {
  forwardRef as forwardRef15,
  useCallback as useCallback7,
  useContext as useContext13,
  useImperativeHandle as useImperativeHandle4,
  useRef as useRef9
} from "react";
import { useOnClickOutside } from "@flywire/react-hooks";

// src/components/Combobox/Context.tsx
import {
  Children as Children2,
  createContext,
  isValidElement as isValidElement2,
  useCallback as useCallback2,
  useEffect as useEffect4,
  useMemo,
  useReducer,
  useRef as useRef4
} from "react";

// src/hooks/useAutoPosition/useAutoPosition.ts
import { useEffect, useState } from "react";

// src/utils/debounce/debounce.ts
function debounce(func, wait) {
  let timeout;
  return function(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// src/hooks/useAutoPosition/useAutoPosition.ts
function useAutoPosition({
  elementRef,
  containerRef,
  defaultPosition,
  offset = 100
}) {
  const [position, setPosition] = useState(defaultPosition);
  useEffect(() => {
    const calculatePosition = () => {
      if (elementRef.current) {
        const elementRect = elementRef.current.getBoundingClientRect();
        const containerRect = containerRef?.current ? containerRef.current.getBoundingClientRect() : {
          top: 0,
          left: 0,
          bottom: window.innerHeight,
          right: window.innerWidth,
          width: window.innerWidth,
          height: window.innerHeight
        };
        const spaceBelow = containerRect.bottom - elementRect.bottom - offset;
        const spaceAbove = elementRect.top - containerRect.top - offset;
        const spaceLeft = elementRect.left - containerRect.left - offset;
        const spaceRight = containerRect.right - elementRect.right - offset;
        const fitsBottom = spaceBelow >= elementRect.height;
        const fitsTop = spaceAbove >= elementRect.height;
        const fitsLeft = spaceLeft >= elementRect.width;
        const fitsRight = spaceRight >= elementRect.width;
        switch (defaultPosition) {
          case "bottom":
            setPosition(fitsBottom ? "bottom" : "top");
            break;
          case "bottom-start":
            setPosition(fitsBottom ? "bottom-start" : "top-start");
            break;
          case "bottom-end":
            setPosition(fitsBottom ? "bottom-end" : "top-end");
            break;
          case "top":
            setPosition(fitsTop ? "top" : "bottom");
            break;
          case "top-start":
            setPosition(fitsTop ? "top-start" : "bottom-start");
            break;
          case "top-end":
            setPosition(fitsTop ? "top-end" : "bottom-end");
            break;
          case "left":
            setPosition(fitsLeft ? "left" : "right");
            break;
          case "left-start":
            setPosition(fitsLeft ? "left-start" : "right-start");
            break;
          case "left-end":
            setPosition(fitsLeft ? "left-end" : "right-end");
            break;
          case "right":
            setPosition(fitsRight ? "right" : "left");
            break;
          case "right-start":
            setPosition(fitsRight ? "right-start" : "left-start");
            break;
          case "right-end":
            setPosition(fitsRight ? "right-end" : "left-end");
            break;
          default:
            setPosition(defaultPosition);
        }
      }
    };
    const debouncedCalculatePosition = debounce(calculatePosition, 100);
    window.addEventListener("scroll", debouncedCalculatePosition, true);
    calculatePosition();
    return () => {
      window.removeEventListener("scroll", debouncedCalculatePosition, true);
    };
  }, [defaultPosition, elementRef, containerRef, offset]);
  return { position };
}

// src/constants/keys.ts
var ARROW_DOWN = "ArrowDown";
var ARROW_LEFT = "ArrowLeft";
var ARROW_RIGHT = "ArrowRight";
var ARROW_UP = "ArrowUp";
var C = "c";
var COMMA = ",";
var DELETE = "Backspace";
var ENTER = "Enter";
var ESCAPE = "Escape";
var SPACE = " ";
var NUMPAD_0 = "0";
var NUMPAD_1 = "1";
var NUMPAD_2 = "2";
var NUMPAD_3 = "3";
var NUMPAD_4 = "4";
var NUMPAD_5 = "5";
var NUMPAD_6 = "6";
var NUMPAD_7 = "7";
var NUMPAD_8 = "8";
var NUMPAD_9 = "9";
var PERIOD = ".";
var SUPR = "Delete";
var TAB = "Tab";
var V = "v";
var X = "x";

// src/hooks/useButton/useButton.ts
function handleOnKeyDown(evt, cb) {
  if (evt.key !== ENTER && evt.key !== SPACE) {
    return;
  }
  if (typeof cb === "function") {
    cb();
  }
}
function useButton({ as, onClick }) {
  switch (as) {
    case "div": {
      return {
        htmlProps: {
          tabIndex: 0,
          role: "button",
          ...typeof onClick === "function" && {
            onKeyDown: (evt) => handleOnKeyDown(evt, onClick),
            onClick
          }
        },
        a11yProps: {}
      };
    }
    case "a": {
      return {
        htmlProps: {
          href: "#",
          role: "button",
          ...typeof onClick === "function" && {
            onKeyDown: (evt) => handleOnKeyDown(evt, onClick),
            onClick
          }
        },
        a11yProps: {}
      };
    }
    default: {
      return {
        htmlProps: {
          type: "button",
          ...typeof onClick === "function" && { onClick }
        },
        a11yProps: {}
      };
    }
  }
}

// src/hooks/useDatePicker/useDatePicker.ts
import { useState as useState2 } from "react";

// src/utils/applyPattern/applyPattern.ts
var applyPattern = ({
  text,
  pattern,
  cursor = text?.length || 0
}) => {
  if (!text || text.length === 0 || !pattern || cursor < text.length) {
    return [text, cursor];
  }
  const sanitizedText = text.replace(/\D/g, "");
  const formattedObject = pattern.split("").reduce(
    (acc, character) => {
      if (acc.remainingText.length === 0) {
        return acc;
      }
      if (character !== ".") {
        return {
          formattedText: acc.formattedText + character,
          remainingText: acc.remainingText
        };
      }
      return {
        formattedText: acc.formattedText + acc.remainingText[0],
        remainingText: acc.remainingText.slice(1)
      };
    },
    { formattedText: "", remainingText: sanitizedText.split("") }
  );
  const formattedText = formattedObject.formattedText;
  const newCursor = formattedText.length;
  return [formattedText, newCursor];
};

// src/utils/callAll/callAll.ts
function callAll(...fns) {
  return function(...args) {
    fns.forEach((fn) => fn?.(...args));
  };
}

// src/utils/formatNumber/formatNumber.ts
function formatNumber(value, { decimals = 2 } = {}) {
  const numberFormat = new Intl.NumberFormat(void 0, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  return numberFormat.format(Math.floor(value * Math.pow(10, decimals)) / Math.pow(10, decimals));
}

// src/utils/parseNumber/parseNumber.ts
function parseNumber(value) {
  if (typeof value === "number") {
    return value;
  }
  return Number.parseFloat(value.replace(/[$,]/g, ""));
}

// src/utils/accounting/accounting.ts
var formatMoney = function(number, options) {
  const format = typeof options.symbol === "string" ? options.format.replace("%s", options.symbol) : options.format;
  return format.replace(
    "%v",
    formatNumber2(
      Math.abs(number),
      checkPrecision(options.precision),
      options.thousand,
      options.decimal
    )
  );
};
var unformat = function(value, decimal) {
  const regex = new RegExp(`[^0-9-${decimal}]`, "g");
  const unformatted = parseFloat(
    value.replace(regex, "").replace(decimal, ".")
    // make sure decimal point is standard
  );
  return !isNaN(unformatted) ? unformatted : 0;
};
function checkPrecision(val) {
  const _val = Math.round(Math.abs(val));
  return isNaN(_val) ? 0 : val;
}
var formatNumber2 = function(number = 0, precision, thousand, decimal) {
  const base = parseInt(toFixed(number, precision), 10) + "";
  const mod = base.length > 3 ? base.length % 3 : 0;
  return (mod ? base.slice(0, mod) + thousand : "") + base.slice(mod).replace(/(\d{3})(?=\d)/g, "$1" + thousand) + (precision ? decimal + toFixed(Math.abs(number), precision).split(".")[1] : "");
};
var toFixed = (value, precision) => {
  precision = checkPrecision(precision);
  const result = Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
  return result.toFixed(precision);
};

// src/utils/formatMoney/money.ts
var defaultOptions = {
  cents: true,
  decimal: ".",
  thousand: ",",
  subunitToUnit: 100,
  symbol: "$",
  symbolFirst: true
};
var toMoney = (number, options) => {
  const config = {
    cents: options?.cents ?? defaultOptions.cents,
    decimal: options?.decimal ?? defaultOptions.decimal,
    thousand: options?.thousand ?? defaultOptions.thousand,
    subunitToUnit: options?.subunitToUnit ?? defaultOptions.subunitToUnit,
    symbol: options?.symbol ?? defaultOptions.symbol,
    symbolFirst: options?.symbolFirst ?? defaultOptions.symbolFirst
  };
  const { cents, decimal, subunitToUnit, symbol, symbolFirst, thousand } = config;
  const precision = cents ? Math.log10(subunitToUnit) : 0;
  let format = "%v";
  if (symbol) {
    format = symbolFirst ? "%s%v" : "%v %s";
  }
  const settings = { format, decimal, symbol, precision, thousand };
  return formatMoney(number, settings);
};
var toNumber = (number, options) => {
  const decimal = options?.decimal ?? defaultOptions.decimal;
  return unformat(number, decimal);
};

// src/utils/formatDate/formatDate.ts
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
dayjs.extend(customParseFormat);
dayjs.extend(utc);
var DEFAULT_DATE_FORMAT = "MM/DD/YYYY";
var NATIVE_FORMAT = "YYYY-MM-DD";
var toNativeDatePickerFormat = (date, dateFormat = DEFAULT_DATE_FORMAT) => {
  return dayjs(date, dateFormat, true).format(NATIVE_FORMAT);
};
var formatDate = (date, dateFormat) => {
  return dayjs(date.toISOString()).utc().format(dateFormat);
};

// src/hooks/useDatePicker/useDatePicker.ts
var useDatePicker = ({
  dateFormat = DEFAULT_DATE_FORMAT,
  name,
  value,
  onBlur,
  onChange
}) => {
  const [nativeValue, setNativeValue] = useState2(
    value ? toNativeDatePickerFormat(value, dateFormat) : ""
  );
  const handleChange = ({
    target: { value: value2, valueAsDate, validity }
  }) => {
    setNativeValue(value2);
    _onChange(validity, valueAsDate);
  };
  const handleBlur = ({
    target: { validity, valueAsDate }
  }) => {
    if (!validity.valid) _onChange(validity, valueAsDate);
    onBlur?.(name);
  };
  const _onChange = (validity, date) => {
    let formattedDate;
    if (isDateInvalid(validity)) {
      formattedDate = "invalid";
    } else if (isDateEmpty(validity, date)) {
      formattedDate = void 0;
    } else {
      formattedDate = formatDate(date, dateFormat);
    }
    onChange?.(name, formattedDate);
  };
  return {
    value: nativeValue,
    onChange: handleChange,
    onBlur: handleBlur
  };
};
var isDateEmpty = (validity, date) => {
  return validity.valueMissing || date === null;
};
var isDateInvalid = (validity) => {
  return validity.badInput;
};

// src/hooks/useId/useId.ts
import { useRef } from "react";
function useId(id = Math.random().toString(36).slice(-5)) {
  const ref = useRef(id);
  return ref.current;
}

// src/hooks/useInput/useInput.ts
function useInput({
  hasHint = false,
  id,
  isInvalid = false,
  isRequired = false
}) {
  return {
    labelProps: {
      htmlFor: id
    },
    inputProps: {
      id,
      name: id
    },
    inputA11yProps: {
      "aria-invalid": isInvalid,
      "aria-required": isRequired,
      "aria-describedby": [isInvalid && `${id}-error`, hasHint && `${id}-description`].filter(Boolean).join(" ")
    }
  };
}

// src/hooks/useMaskInput/useMaskInput.ts
import { useRef as useRef3, useState as useState3 } from "react";

// src/hooks/useRunAfterUpdate/useRunAfterUpdate.ts
import { useEffect as useEffect2, useRef as useRef2 } from "react";
function useRunAfterUpdate() {
  const afterPaintRef = useRef2();
  useEffect2(() => {
    if (afterPaintRef.current) {
      afterPaintRef.current?.();
      afterPaintRef.current = void 0;
    }
  });
  return (fn) => afterPaintRef.current = fn;
}

// src/hooks/useMaskInput/useMaskInput.ts
var NO_VALUE = "";
function useMaskInput({
  initialValue = NO_VALUE,
  mask,
  onChange = () => {
  },
  ...rest
}) {
  const inputRef = useRef3(null);
  const [value, setValue] = useState3(initialValue);
  const runAfterUpdate = useRunAfterUpdate();
  function handleInputChange(evt) {
    const input = inputRef.current;
    const [formattedText, newCursor] = applyPattern({
      text: evt.target.value,
      pattern: mask,
      cursor: evt.target.selectionStart || 0
    });
    if (formattedText !== void 0) {
      setValue(formattedText);
      runAfterUpdate(() => {
        if (!input) {
          return;
        }
        input.selectionStart = newCursor;
        input.selectionEnd = newCursor;
      });
      const newEvent = createUserEvent(evt, formattedText);
      onChange?.(newEvent);
    }
  }
  return {
    inputProps: {
      onChange: handleInputChange,
      value,
      ref: inputRef,
      maxLength: mask ? mask.length : void 0,
      ...rest
    }
  };
}
function createUserEvent(origEvent, newValue) {
  return {
    ...origEvent,
    target: {
      ...origEvent.target,
      value: newValue
    },
    currentTarget: {
      ...origEvent.currentTarget,
      value: newValue
    }
  };
}

// src/hooks/useMoneyInput/useMoneyInput.ts
import {
  useCallback,
  useEffect as useEffect3,
  useState as useState4
} from "react";
var NO_VALUE2 = "";
var DEFAULT_DECIMALS = 2;
function useMoneyInput({
  decimals = DEFAULT_DECIMALS,
  decimalMark,
  onChange = () => {
  },
  onBlur = () => {
  },
  onKeyDown = () => {
  },
  onValueBlur,
  onValueChange,
  initialValue = NO_VALUE2,
  subunitToUnit,
  thousandsSeparator,
  ...rest
} = {}) {
  const [value, setValue] = useState4(initialValue);
  const hasCustomFormatter = decimalMark !== void 0 && thousandsSeparator !== void 0;
  function handleInputKeyDown(evt) {
    const isControl = evt.ctrlKey || evt.metaKey;
    const isCopy = isControl && evt.key === C;
    const isCut = isControl && evt.key === X;
    const isNumber = /\d/.test(evt.key);
    const isPaste = isControl && evt.key === V;
    const allowedChars = [
      COMMA,
      PERIOD,
      DELETE,
      ARROW_LEFT,
      NUMPAD_0,
      NUMPAD_1,
      NUMPAD_2,
      NUMPAD_3,
      NUMPAD_4,
      NUMPAD_5,
      NUMPAD_6,
      NUMPAD_7,
      NUMPAD_8,
      NUMPAD_9,
      PERIOD,
      ARROW_RIGHT,
      SUPR,
      TAB
    ].includes(evt.key);
    if (!isNumber && !allowedChars && !isPaste && !isCopy && !isCut) {
      evt.preventDefault();
    }
  }
  function handleInputChange(evt) {
    setValue(evt.target.value);
    onValueChange?.(parse(format(evt.target.value)));
  }
  function handleInputBlur() {
    setValue((value2) => {
      const _value = value2 === NO_VALUE2 ? value2 : format(value2);
      onValueBlur?.(parse(_value));
      return _value;
    });
  }
  const format = useCallback(
    (value2) => {
      if (hasCustomFormatter) {
        const options = {
          decimal: decimalMark,
          thousand: thousandsSeparator,
          symbol: false,
          subunitToUnit
        };
        return toMoney(
          typeof value2 === "string" ? toNumber(value2.toString(), options) : value2,
          options
        );
      }
      return formatNumber(parseNumber(value2), { decimals });
    },
    [decimalMark, decimals, hasCustomFormatter, subunitToUnit, thousandsSeparator]
  );
  function parse(value2) {
    return hasCustomFormatter ? toNumber(value2.toString(), {
      decimal: decimalMark
    }) : parseNumber(value2);
  }
  useEffect3(
    () => setValue(initialValue === NO_VALUE2 ? NO_VALUE2 : format(initialValue)),
    [initialValue, decimals, format]
  );
  return {
    inputProps: {
      onBlur: callAll(onBlur, handleInputBlur),
      onChange: callAll(onChange, handleInputChange),
      onKeyDown: callAll(onKeyDown, handleInputKeyDown),
      type: "text",
      value,
      ...rest
    }
  };
}

// src/utils/findChildRecursively/findChildRecursively.ts
import { Children, isValidElement } from "react";
var findChildRecursively = (children, callback) => {
  return Children.toArray(children).some((child) => {
    if (isValidElement(child)) {
      if (callback(child)) {
        return true;
      }
      if (child.props.children) {
        return findChildRecursively(child.props.children, callback);
      }
    }
    return false;
  });
};

// src/components/Combobox/constants.ts
var DECREMENT_ACTIVE_OPTION_INDEX = "DECREMENT_ACTIVE_OPTION_INDEX";
var INCREMENT_ACTIVE_OPTION_INDEX = "INCREMENT_ACTIVE_OPTION_INDEX";
var SELECT_ACTIVE_OPTION = "SELECT_ACTIVE_OPTION";
var SET_ACTIVE_OPTION_INDEX = "SET_ACTIVE_OPTION_INDEX";
var SET_INPUT_VALUE = "SET_INPUT_VALUE";
var SET_IS_DISABLED = "SET_IS_DISABLED";
var SET_IS_INVALID = "SET_IS_INVALID";
var SET_IS_OPEN = "SET_IS_OPEN";
var SET_IS_REQUIRED = "SET_IS_REQUIRED";
var SET_OPTIONS = "SET_OPTIONS";
var SET_SELECTED_VALUE = "SET_SELECTED_VALUE";
var DISPLAY_NAME_COMBOBOX = "Combobox";
var DISPLAY_NAME_ERROR = "Error";
var DISPLAY_NAME_FIELD = "Field";
var DISPLAY_NAME_INPUT = "Input";
var DISPLAY_NAME_LABEL = "Label";
var DISPLAY_NAME_OPTION = "Option";
var DISPLAY_NAME_OPTIONS = "Options";
var DISPLAY_NAME_WRAPPER = "Wrapper";
var SET_IS_NAVIGATING_WITH_KEYBOARD = "SET_IS_NAVIGATING_WITH_KEYBOARD";

// src/components/Combobox/Context.tsx
import { jsx } from "react/jsx-runtime";
var DEFAULT_ACTIVE_OPTION_INDEX = -1;
var Context = createContext({
  activeOptionIndex: DEFAULT_ACTIVE_OPTION_INDEX,
  decrementActiveOptionIndex: () => {
  },
  id: "",
  incrementActiveOptionIndex: () => {
  },
  inputValue: "",
  isOpen: false,
  isRequired: false,
  isInvalid: false,
  selectActiveOption: () => {
  },
  selectedValue: null,
  setInputValue: () => {
  },
  setIsOpen: () => {
  },
  setSelectedValue: () => {
  },
  setActiveOptionIndex: () => {
  },
  setIsNavigatingWithKeyboard: () => {
  },
  isNavigatingWithKeyboard: false
});
var initialState = {
  activeOptionIndex: DEFAULT_ACTIVE_OPTION_INDEX,
  inputValue: "",
  isDisabled: false,
  isInvalid: false,
  isRequired: false,
  isOpen: false,
  options: [],
  selectedValue: null,
  isNavigatingWithKeyboard: false
};
function reducer(state, action) {
  switch (action.type) {
    case SET_IS_OPEN:
      if (state.isDisabled || state.isOpen === action.payload) {
        return state;
      }
      return {
        ...state,
        activeOptionIndex: !action.payload && state.selectedValue ? state.options.findIndex((option) => option.id === state.selectedValue?.id) : state.activeOptionIndex,
        isOpen: action.payload
      };
    case SET_SELECTED_VALUE: {
      if (!action.payload) return state;
      const selectedOptionIndex = state.options.findIndex((option) => option.name === action.payload);
      const selectedOption = state.options[selectedOptionIndex] || null;
      return {
        ...state,
        activeOptionIndex: selectedOptionIndex,
        inputValue: action.payload || "",
        selectedValue: selectedOption
      };
    }
    case SET_INPUT_VALUE:
      return {
        ...state,
        inputValue: action.payload
      };
    case INCREMENT_ACTIVE_OPTION_INDEX:
      if (!state.isOpen) {
        return state;
      }
      return {
        ...state,
        activeOptionIndex: Math.min(state.activeOptionIndex + 1, state.options.length - 1),
        isNavigatingWithKeyboard: true
      };
    case DECREMENT_ACTIVE_OPTION_INDEX:
      if (!state.isOpen) {
        return state;
      }
      return {
        ...state,
        activeOptionIndex: Math.max(state.activeOptionIndex - 1, 0),
        isNavigatingWithKeyboard: true
      };
    case SET_OPTIONS:
      return { ...state, options: action.payload };
    case SELECT_ACTIVE_OPTION:
      if (state.activeOptionIndex >= 0 && state.activeOptionIndex < state.options.length) {
        const selectedValue = state.options[state.activeOptionIndex];
        return {
          ...state,
          inputValue: selectedValue.name,
          isOpen: false,
          selectedValue
        };
      }
      return state;
    case SET_ACTIVE_OPTION_INDEX:
      return {
        ...state,
        activeOptionIndex: action.payload
      };
    case SET_IS_DISABLED:
      return { ...state, isDisabled: action.payload };
    case SET_IS_REQUIRED:
      return { ...state, isRequired: action.payload };
    case SET_IS_INVALID:
      return { ...state, isInvalid: action.payload };
    case SET_IS_NAVIGATING_WITH_KEYBOARD:
      if (state.isNavigatingWithKeyboard === action.payload) {
        return state;
      }
      return {
        ...state,
        isNavigatingWithKeyboard: action.payload
      };
    default:
      return state;
  }
}
function Provider({
  id: customId,
  children,
  onClose = () => {
  },
  onChange = () => {
  },
  ...rest
}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const id = useId(customId);
  const isMounted = useRef4(false);
  useEffect4(() => {
    if (isMounted.current) {
      if (!state.isOpen) {
        onClose();
      }
    } else {
      isMounted.current = true;
    }
  }, [state.isOpen, onClose]);
  useEffect4(() => {
    if (isMounted.current) {
      onChange(state.selectedValue);
    }
  }, [state.selectedValue, onChange]);
  useEffect4(() => {
    dispatch({ type: SET_IS_INVALID, payload: initialState.isInvalid });
    const options = [];
    const checkChild = (child) => {
      if (isValidElement2(child)) {
        const { displayName } = child.type;
        if (displayName === DISPLAY_NAME_OPTION) options.push(child.props.value);
        if (child.props.children) {
          Children2.forEach(child.props.children, checkChild);
        }
        return true;
      }
      return false;
    };
    findChildRecursively(children, checkChild);
    dispatch({ type: SET_OPTIONS, payload: options });
  }, [children]);
  useEffect4(() => {
    const checkChild = (child) => {
      if (isValidElement2(child)) {
        const { displayName } = child.type;
        if (displayName === DISPLAY_NAME_INPUT) {
          const { defaultValue, disabled, required } = child.props;
          if (defaultValue) dispatch({ type: SET_SELECTED_VALUE, payload: defaultValue });
          if (disabled) dispatch({ type: SET_IS_DISABLED, payload: true });
          if (required) dispatch({ type: SET_IS_REQUIRED, payload: true });
        }
        if (displayName === DISPLAY_NAME_ERROR) {
          dispatch({ type: SET_IS_INVALID, payload: true });
        }
        if (child.props.children) {
          Children2.forEach(child.props.children, checkChild);
        }
        return true;
      }
      return false;
    };
    findChildRecursively(children, checkChild);
  }, [children]);
  const decrementActiveOptionIndex = useCallback2(
    () => dispatch({ type: DECREMENT_ACTIVE_OPTION_INDEX }),
    []
  );
  const incrementActiveOptionIndex = useCallback2(
    () => dispatch({ type: INCREMENT_ACTIVE_OPTION_INDEX }),
    []
  );
  const selectActiveOption = useCallback2(() => dispatch({ type: SELECT_ACTIVE_OPTION }), []);
  const setInputValue = useCallback2(
    (value2) => dispatch({ type: SET_INPUT_VALUE, payload: value2 }),
    []
  );
  const setIsOpen = useCallback2(
    (isOpen) => dispatch({ type: SET_IS_OPEN, payload: isOpen }),
    []
  );
  const setSelectedValue = useCallback2(
    (value2) => dispatch({ type: SET_SELECTED_VALUE, payload: value2 }),
    []
  );
  const setActiveOptionIndex = useCallback2(
    (index) => dispatch({ type: SET_ACTIVE_OPTION_INDEX, payload: index }),
    []
  );
  const setIsNavigatingWithKeyboard = useCallback2(
    (isNavigating) => dispatch({ type: SET_IS_NAVIGATING_WITH_KEYBOARD, payload: isNavigating }),
    []
  );
  const value = useMemo(
    () => ({
      decrementActiveOptionIndex,
      id,
      incrementActiveOptionIndex,
      selectActiveOption,
      setInputValue,
      setIsOpen,
      setSelectedValue,
      setActiveOptionIndex,
      setIsNavigatingWithKeyboard,
      ...state
    }),
    [
      id,
      state,
      decrementActiveOptionIndex,
      incrementActiveOptionIndex,
      selectActiveOption,
      setInputValue,
      setIsOpen,
      setSelectedValue,
      setActiveOptionIndex,
      setIsNavigatingWithKeyboard
    ]
  );
  return /* @__PURE__ */ jsx(Context.Provider, { value, ...rest, children });
}

// src/components/Combobox/Error.tsx
import { forwardRef, useContext } from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var ComboboxError = forwardRef(
  ({ as: Element = "p", children, render, className, ...rest }, ref) => {
    const { id, isInvalid } = useContext(Context);
    return /* @__PURE__ */ jsx2(Element, { ref, id: `${id}-error`, role: "alert", className, ...rest, children: render ? render({ isInvalid }) : children });
  }
);
ComboboxError.displayName = "Error";

// src/components/Combobox/Field.tsx
import { forwardRef as forwardRef10, useContext as useContext8, useMemo as useMemo4 } from "react";

// src/components/Field/Field.tsx
import { forwardRef as forwardRef9 } from "react";

// src/components/Field/Context.tsx
import {
  Children as Children3,
  createContext as createContext2,
  isValidElement as isValidElement3,
  useEffect as useEffect5,
  useMemo as useMemo2,
  useState as useState5
} from "react";

// src/constants/index.ts
var DESCRIPTION = "Description";
var ERROR = "Error";
var FILE = "File";
var GROUP = "Group";
var HINT = "Hint";
var INPUT = "Input";
var LABEL = "Label";
var TEXTAREA = "Textarea";
var TITLE = "Title";

// src/components/Field/Context.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
function findChildRecursively2(children, callback) {
  return Children3.toArray(children).some((child) => {
    if (isValidElement3(child)) {
      if (callback(child)) {
        return true;
      }
      if (child.props.children) {
        return findChildRecursively2(child.props.children, callback);
      }
    }
    return false;
  });
}
var Context2 = createContext2({
  hasHint: false,
  id: "",
  isInvalid: false,
  isLoading: false,
  isRequired: false,
  setIsLoading: () => {
  }
});
function Provider2({ id: customId, children, ...rest }) {
  const [isLoading, setIsLoading] = useState5(false);
  const id = useId(customId);
  const [hasHint, setHasHint] = useState5(false);
  const [isInvalid, setIsInvalid] = useState5(false);
  const [isRequired, setIsRequired] = useState5(false);
  useEffect5(() => {
    const checkChild = (child) => {
      setIsInvalid(false);
      if (isValidElement3(child)) {
        const { displayName } = child.type;
        if (displayName === ERROR) {
          setIsInvalid(true);
          return true;
        } else if (displayName === HINT) {
          setHasHint(true);
        } else if (displayName === INPUT || displayName === TEXTAREA) {
          setIsRequired(!!child.props.required);
        }
        if (child.props.children) {
          return findChildRecursively2(child.props.children, checkChild);
        }
      }
      return false;
    };
    findChildRecursively2(children, checkChild);
  }, [children]);
  const value = useMemo2(
    () => ({
      hasHint,
      id,
      isInvalid,
      isLoading,
      isRequired,
      setIsLoading
    }),
    [id, isLoading, hasHint, isInvalid, isRequired]
  );
  return /* @__PURE__ */ jsx3(Context2.Provider, { value, ...rest, children });
}

// src/components/Field/components/Error.tsx
import { forwardRef as forwardRef2, useContext as useContext2 } from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
var FieldError = forwardRef2(
  ({ as: Element = "p", children, render, ...rest }, ref) => {
    const { id, isInvalid, isLoading, isRequired } = useContext2(Context2);
    return /* @__PURE__ */ jsx4(Element, { ref, id: `${id}-error`, role: "alert", ...rest, children: render ? render({ isInvalid, isLoading, isRequired }) : children });
  }
);
FieldError.displayName = ERROR;

// src/components/Field/components/File.tsx
import { forwardRef as forwardRef4, useContext as useContext4, useImperativeHandle, useRef as useRef5 } from "react";

// src/components/Field/components/Input.tsx
import { forwardRef as forwardRef3, useContext as useContext3 } from "react";
import { jsx as jsx5 } from "react/jsx-runtime";
var Input = forwardRef3((props, ref) => {
  const context = useContext3(Context2);
  const { inputProps, inputA11yProps } = useInput(context);
  return /* @__PURE__ */ jsx5("input", { ref, ...inputProps, ...inputA11yProps, ...props });
});
Input.displayName = INPUT;

// src/components/Field/components/File.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var DEFAULT_MAX_FILE_SIZE_IN_BYTES = 8e6;
var WAIT_TIME = 250;
var InvalidFileSizeError = class extends Error {
};
var File = forwardRef4(
  ({
    accept = ".png,.jpg,.jpeg,.pdf",
    maxFileSizeInBytes = DEFAULT_MAX_FILE_SIZE_IN_BYTES,
    onChange = () => Promise.resolve(),
    onError = () => {
    },
    ...rest
  }, ref) => {
    const { setIsLoading } = useContext4(Context2);
    const inputFileRef = useRef5(null);
    useImperativeHandle(ref, () => inputFileRef.current);
    async function handleOnChange(evt) {
      setIsLoading(true);
      const file = evt.target.files?.[0];
      const invalidFileSize = file && file.size > maxFileSizeInBytes;
      if (invalidFileSize) {
        inputFileRef?.current?.setCustomValidity("Invalid file size");
        setIsLoading(false);
        return onError(new InvalidFileSizeError("Invalid file size"));
      }
      inputFileRef?.current?.setCustomValidity("");
      await Promise.allSettled([
        onChange(evt),
        new Promise((resolve) => setTimeout(resolve, WAIT_TIME))
      ]);
      setIsLoading(false);
      evt.target.value = "";
    }
    return /* @__PURE__ */ jsx6(Input, { accept, onChange: handleOnChange, ref: inputFileRef, ...rest, type: "file" });
  }
);
File.displayName = FILE;

// src/components/Field/components/Group.tsx
import {
  Children as Children4,
  Fragment,
  cloneElement,
  forwardRef as forwardRef5,
  isValidElement as isValidElement4,
  useState as useState6
} from "react";
import { jsx as jsx7 } from "react/jsx-runtime";
var isInteractiveElement = (element) => {
  const interactiveTypes = ["input", "textarea", "select", "button"];
  const interactiveProps = ["onChange", "onClick", "onInput", "onBlur"];
  if (interactiveTypes.includes(element?.type)) {
    return true;
  }
  if (element?.props) {
    return interactiveProps.some((prop) => prop in element.props);
  }
  return false;
};
var initializeValues = (children) => {
  const initialValues = [];
  const initialize = (child) => {
    if (!child) {
      return;
    }
    if (isInteractiveElement(child)) {
      const { defaultValue, value, selected } = child.props;
      initialValues.push(defaultValue || value || selected || "");
    } else if (child.props?.children) {
      Children4.forEach(child.props.children, initialize);
    }
  };
  Children4.forEach(children, initialize);
  return initialValues;
};
var Group = forwardRef5(function Group2({
  as: Element = Fragment,
  children,
  className,
  onChange = () => {
  },
  onBlur = () => {
  },
  ...rest
}, ref) {
  let indexRef = 0;
  const [values, setValues] = useState6(() => initializeValues(children));
  function handleOnChange({
    evt,
    index
  }) {
    const newValues = [...values];
    newValues[index] = evt.target?.value;
    setValues(newValues);
    const event = new CustomEvent("change", {
      detail: { values: newValues }
    });
    onChange(event);
  }
  function handleOnBlur() {
    const event = new CustomEvent("blur", {
      detail: { values }
    });
    onBlur(event);
  }
  function cloneWithHandlers(child) {
    if (!child) {
      return child;
    }
    if (!isInteractiveElement(child)) {
      if (child.props?.children) {
        return cloneElement(child, {
          children: Children4.map(
            child.props.children,
            (nestedChild) => cloneWithHandlers(nestedChild)
          )
        });
      }
      return child;
    }
    const { onChange: onChange2, onBlur: onBlur2 } = child.props;
    const currentIndex = indexRef;
    const clonedElement = cloneElement(child, {
      onChange: callAll(
        onChange2,
        (evt) => handleOnChange({ evt, index: currentIndex })
      ),
      onBlur: callAll(onBlur2, handleOnBlur)
    });
    indexRef += 1;
    return clonedElement;
  }
  return /* @__PURE__ */ jsx7(Element, { ...rest, ...Element !== Fragment && { className, ref }, children: Children4.map(children, (child) => isValidElement4(child) ? cloneWithHandlers(child) : child) });
});
Group.displayName = GROUP;

// src/components/Field/components/Hint.tsx
import { forwardRef as forwardRef6, useContext as useContext5 } from "react";
import { jsx as jsx8 } from "react/jsx-runtime";
var Hint = forwardRef6(
  ({ as: Element = "p", children, render, ...rest }, ref) => {
    const { id, isInvalid, isLoading, isRequired } = useContext5(Context2);
    return /* @__PURE__ */ jsx8(Element, { ref, id: `${id}-description`, ...rest, children: render ? render({ isInvalid, isLoading, isRequired }) : children });
  }
);
Hint.displayName = HINT;

// src/components/Field/components/Label.tsx
import {
  Fragment as Fragment2,
  forwardRef as forwardRef7,
  useContext as useContext6
} from "react";
import { jsx as jsx9, jsxs } from "react/jsx-runtime";
var Label = forwardRef7(({ children, render, ...rest }, ref) => {
  const context = useContext6(Context2);
  const { isInvalid, isLoading, isRequired } = context;
  const { labelProps } = useInput(context);
  return /* @__PURE__ */ jsx9("label", { ref, ...labelProps, ...rest, children: render ? render({ isInvalid, isLoading, isRequired }) : isRequired ? /* @__PURE__ */ jsxs(Fragment2, { children: [
    children,
    /* @__PURE__ */ jsx9("span", { "aria-hidden": true, children: " *" })
  ] }) : children });
});
Label.displayName = LABEL;

// src/components/Field/components/Textarea.tsx
import { forwardRef as forwardRef8, useContext as useContext7 } from "react";
import { jsx as jsx10 } from "react/jsx-runtime";
var Textarea = forwardRef8(
  (props, ref) => {
    const context = useContext7(Context2);
    const { inputProps, inputA11yProps } = useInput(context);
    return /* @__PURE__ */ jsx10("textarea", { ref, ...inputProps, ...inputA11yProps, ...props });
  }
);
Textarea.displayName = TEXTAREA;

// src/components/Field/components/Checkbox/Checkbox.tsx
import { Children as Children5, cloneElement as cloneElement2, isValidElement as isValidElement5, useCallback as useCallback3, useMemo as useMemo3, useRef as useRef6 } from "react";

// src/components/Field/components/Checkbox/components/CheckboxIndicator.tsx
import { Fragment as Fragment3, jsx as jsx11 } from "react/jsx-runtime";
var CHECKBOX_INDICATOR = "CheckboxIndicator";
var DefaultIcon = ({ color }) => /* @__PURE__ */ jsx11("svg", { width: "12", height: "9", xmlns: "http://www.w3.org/2000/svg", children: /* @__PURE__ */ jsx11(
  "path",
  {
    d: "M1 5.013L3.985 8 11 1",
    stroke: color || "#000",
    strokeWidth: "2",
    fill: "none",
    fillRule: "evenodd",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }
) });
var CheckboxIndicator = (props) => {
  if (!props.checked) {
    return null;
  }
  if (props.style || props.className) {
    return /* @__PURE__ */ jsx11(
      "span",
      {
        style: props.style ? props.style : {},
        className: props.className ? props.className : "",
        children: props.children || /* @__PURE__ */ jsx11(DefaultIcon, { color: props.color })
      }
    );
  }
  return /* @__PURE__ */ jsx11(Fragment3, { children: props.children || /* @__PURE__ */ jsx11(DefaultIcon, { color: props.color }) });
};
CheckboxIndicator.displayName = CHECKBOX_INDICATOR;

// src/components/Field/components/Checkbox/Checkbox.tsx
import { Fragment as Fragment4, jsx as jsx12, jsxs as jsxs2 } from "react/jsx-runtime";
var CheckboxValue = {
  checked: "checked",
  unchecked: "unchecked"
};
var Checkbox = (props) => {
  const { children, onChange, checked = false, ariaLabelledBy } = props;
  const checkboxRef = useRef6(null);
  const onClickHandler = useCallback3(
    () => onChange?.(checkboxRef.current?.value === CheckboxValue.unchecked),
    [onChange, checkboxRef]
  );
  const _indicator = useMemo3(() => {
    return Children5.map(children, (child) => {
      if (isValidElement5(child) && child.type === CheckboxIndicator) {
        return cloneElement2(child, { checked, ...child.props });
      }
    });
  }, [children, checked]);
  const _label = useMemo3(() => {
    return Children5.map(children, (child) => {
      if (isValidElement5(child) && child.type === Label) {
        return cloneElement2(child, {
          ...child.props,
          onClick: onClickHandler
        });
      }
    });
  }, [children, onClickHandler]);
  return /* @__PURE__ */ jsxs2(Fragment4, { children: [
    /* @__PURE__ */ jsx12(
      "button",
      {
        ref: checkboxRef,
        type: "button",
        role: "checkbox",
        "aria-checked": checked,
        "aria-labelledby": ariaLabelledBy,
        value: checked ? CheckboxValue.checked : CheckboxValue.unchecked,
        className: props.className,
        onClick: onClickHandler,
        style: props.style,
        "data-testid": props["data-testid"],
        name: props.name,
        children: _indicator
      }
    ),
    _label
  ] });
};

// src/components/Field/Field.tsx
import { jsx as jsx13 } from "react/jsx-runtime";
var FieldComponent = forwardRef9(
  ({ as: Element = "div", className, id, children, ...rest }, ref) => {
    return /* @__PURE__ */ jsx13(Element, { ref, className, children: /* @__PURE__ */ jsx13(Provider2, { id, ...rest, children }) });
  }
);
FieldComponent.displayName = "Field";
var Field = FieldComponent;
Field.displayName = "Field";
Field.Error = FieldError;
Field.File = File;
Field.Group = Group;
Field.Hint = Hint;
Field.Input = Input;
Field.Label = Label;
Field.Textarea = Textarea;
Field.Checkbox = Checkbox;
Field.CheckboxIndicator = CheckboxIndicator;

// src/components/Combobox/Field.tsx
import { jsx as jsx14 } from "react/jsx-runtime";
var Field2 = forwardRef10(
  (props, ref) => {
    const { id } = useContext8(Context);
    const memoizedId = useMemo4(() => id, [id]);
    return /* @__PURE__ */ jsx14(Field, { ...props, id: memoizedId, ref });
  }
);
Field2.displayName = DISPLAY_NAME_FIELD;

// src/components/Combobox/Input.tsx
import {
  forwardRef as forwardRef11,
  useCallback as useCallback4,
  useContext as useContext9,
  useEffect as useEffect7,
  useImperativeHandle as useImperativeHandle2,
  useMemo as useMemo6,
  useRef as useRef7,
  useState as useState7
} from "react";

// src/hooks/useKeys/useKeys.ts
import { useEffect as useEffect6, useMemo as useMemo5 } from "react";
function useKeys(keys, handler, ref) {
  const keyList = useMemo5(() => Array.isArray(keys) ? keys : [keys], [keys]);
  useEffect6(() => {
    const element = ref.current;
    if (!element) return;
    const handleKeyDown = (event) => {
      if (keyList.includes(event.key)) {
        handler(event);
      }
    };
    element.addEventListener("keydown", handleKeyDown);
    return () => {
      element.removeEventListener("keydown", handleKeyDown);
    };
  }, [keyList, handler, ref]);
}
var useKeys_default = useKeys;

// src/components/Combobox/Input.tsx
import { jsx as jsx15 } from "react/jsx-runtime";
var Input2 = forwardRef11(
  ({ placeholder, onClick = () => {
  }, onChange = () => {
  }, defaultValue, ...rest }, ref) => {
    const {
      activeOptionIndex,
      decrementActiveOptionIndex,
      id,
      incrementActiveOptionIndex,
      isOpen,
      selectActiveOption,
      selectedValue,
      setInputValue,
      setIsOpen
    } = useContext9(Context);
    const inputRef = useRef7(null);
    const initialValue = useMemo6(() => `${defaultValue}` || "", [defaultValue]);
    const [value, setValue] = useState7(initialValue);
    useImperativeHandle2(ref, () => inputRef.current);
    useEffect7(() => {
      if (defaultValue) {
        setValue(`${defaultValue}`);
      }
    }, [defaultValue]);
    useEffect7(() => {
      if (selectedValue) {
        setValue(selectedValue.name);
      }
    }, [selectedValue]);
    const handleOnClick = useCallback4(() => {
      setIsOpen(true);
    }, [setIsOpen]);
    const handleOnChange = useCallback4(
      (evt) => {
        setIsOpen(true);
        setValue(evt.target.value);
        setInputValue(evt.target.value);
        if (onChange) {
          onChange(evt);
        }
      },
      [setIsOpen, setInputValue, onChange]
    );
    const handleOnBlur = useCallback4(() => {
      if (selectedValue) {
        setValue(selectedValue.name);
      }
    }, [selectedValue]);
    const handleKeyDown = useCallback4(
      (evt) => {
        if (evt.key === TAB || evt.key === ESCAPE) {
          setIsOpen(false);
        } else if (evt.key === ENTER) {
          if (isOpen) {
            selectActiveOption();
          } else {
            setIsOpen(true);
          }
        }
      },
      [isOpen, setIsOpen, selectActiveOption]
    );
    const handleArrowKeys = useCallback4(
      (evt) => {
        evt.preventDefault();
        if (evt.key === ARROW_DOWN) {
          incrementActiveOptionIndex();
        } else if (evt.key === ARROW_UP) {
          decrementActiveOptionIndex();
        }
      },
      [incrementActiveOptionIndex, decrementActiveOptionIndex]
    );
    useKeys_default([TAB, ESCAPE, ENTER], handleKeyDown, inputRef);
    useKeys_default([ARROW_DOWN, ARROW_UP], handleArrowKeys, inputRef);
    return /* @__PURE__ */ jsx15(
      Field.Input,
      {
        ...rest,
        "aria-activedescendant": activeOptionIndex >= 0 ? `${id}-option-${activeOptionIndex}` : void 0,
        "aria-autocomplete": "list",
        "aria-controls": `${id}-options`,
        "aria-expanded": isOpen,
        "aria-labelledby": `${id}-label`,
        autoComplete: "off",
        "data-testid": `${id}-input`,
        id: `${id}-input`,
        onBlur: handleOnBlur,
        onChange: callAll(onChange, handleOnChange),
        onClick: callAll(onClick, handleOnClick),
        placeholder,
        ref: inputRef,
        role: "combobox",
        value
      }
    );
  }
);
Input2.displayName = DISPLAY_NAME_INPUT;

// src/components/Combobox/Label.tsx
import { forwardRef as forwardRef12, useContext as useContext10, useMemo as useMemo7 } from "react";
import { jsx as jsx16 } from "react/jsx-runtime";
var Label2 = forwardRef12((props, ref) => {
  const { id } = useContext10(Context);
  const htmlForId = useMemo7(() => `${id}-input`, [id]);
  return /* @__PURE__ */ jsx16(Field.Label, { ...props, htmlFor: htmlForId, ref });
});
Label2.displayName = DISPLAY_NAME_LABEL;

// src/components/Combobox/Option.tsx
import {
  forwardRef as forwardRef13,
  useCallback as useCallback5,
  useContext as useContext11,
  useMemo as useMemo8
} from "react";
import { jsx as jsx17 } from "react/jsx-runtime";
var Option = forwardRef13(
  ({
    as: Element = "li",
    className,
    children,
    index,
    isActive = false,
    isSelected = false,
    render,
    value,
    ...rest
  }, ref) => {
    const { id, setActiveOptionIndex, isNavigatingWithKeyboard } = useContext11(Context);
    const optionId = useMemo8(() => `${id}-option-${value.id}`, [id, value.id]);
    const handleMouseEnter = useCallback5(() => {
      if (index !== void 0) {
        setActiveOptionIndex(index);
      }
    }, [index, setActiveOptionIndex]);
    const optionClasses = useMemo8(() => {
      if (typeof className === "function") {
        return className({ isActive, isSelected });
      }
      return className;
    }, [className, isActive, isSelected]);
    return /* @__PURE__ */ jsx17(
      Element,
      {
        ...rest,
        "aria-selected": isSelected,
        className: optionClasses,
        "data-active": isActive,
        "data-pointer-disabled": isNavigatingWithKeyboard,
        "data-selected": isSelected,
        "data-testid": optionId,
        id: optionId,
        onMouseEnter: handleMouseEnter,
        ref,
        role: "option",
        tabIndex: -1,
        children: render ? render({ isActive, isSelected }) : children
      }
    );
  }
);
Option.displayName = DISPLAY_NAME_OPTION;

// src/components/Combobox/Options.tsx
import {
  Children as Children6,
  cloneElement as cloneElement3,
  forwardRef as forwardRef14,
  isValidElement as isValidElement6,
  useCallback as useCallback6,
  useContext as useContext12,
  useEffect as useEffect8,
  useImperativeHandle as useImperativeHandle3,
  useMemo as useMemo9,
  useRef as useRef8
} from "react";
import { jsx as jsx18 } from "react/jsx-runtime";
var Options = forwardRef14(
  ({ as: Element = "ul", children, ...rest }, forwardedRef) => {
    const {
      id,
      isOpen,
      activeOptionIndex,
      selectActiveOption,
      setIsOpen,
      setIsNavigatingWithKeyboard,
      selectedValue
    } = useContext12(Context);
    const internalRef = useRef8(null);
    useImperativeHandle3(
      forwardedRef,
      () => ({
        scrollToActiveOption: () => {
          if (internalRef.current && activeOptionIndex >= 0) {
            const activeOption = internalRef.current.children[activeOptionIndex];
            if (activeOption) {
              activeOption.scrollIntoView({ block: "nearest" });
            }
          }
        }
      }),
      [activeOptionIndex]
    );
    useEffect8(() => {
      if (internalRef.current && activeOptionIndex >= 0) {
        const activeOption = internalRef.current.children[activeOptionIndex];
        if (activeOption) {
          activeOption.scrollIntoView({ block: "nearest" });
        }
      }
    }, [activeOptionIndex]);
    const handleClick = useCallback6(() => {
      selectActiveOption();
      setIsOpen(false);
    }, [selectActiveOption, setIsOpen]);
    const handleMouseMove = useCallback6(() => {
      setIsNavigatingWithKeyboard(false);
    }, [setIsNavigatingWithKeyboard]);
    const renderedChildren = useMemo9(() => {
      if (!isOpen) return null;
      return Children6.map(children, (child, index) => {
        if (isValidElement6(child)) {
          const isActive = activeOptionIndex === index;
          const isSelected = selectedValue?.id === child.props.value.id;
          return cloneElement3(child, {
            index,
            isActive,
            isSelected,
            key: child.key ?? child.props.value.id
          });
        }
        return child;
      });
    }, [children, isOpen, activeOptionIndex, selectedValue]);
    return /* @__PURE__ */ jsx18(
      Element,
      {
        ...rest,
        "aria-labelledby": `${id}-label`,
        "data-testid": `${id}-options`,
        id: `${id}-options`,
        ref: internalRef,
        role: "listbox",
        onClick: handleClick,
        onMouseMove: handleMouseMove,
        children: renderedChildren
      }
    );
  }
);
Options.displayName = DISPLAY_NAME_OPTIONS;

// src/components/Combobox/Combobox.tsx
import { jsx as jsx19 } from "react/jsx-runtime";
var ComboboxComponent = forwardRef15(
  ({ as: Element = "div", children, className }, forwardedRef) => {
    const internalRef = useRef9(null);
    const { isOpen, setIsOpen } = useContext13(Context);
    useImperativeHandle4(forwardedRef, () => internalRef.current);
    useOnClickOutside(
      internalRef,
      useCallback7(() => setIsOpen(false), [setIsOpen])
    );
    return /* @__PURE__ */ jsx19(Element, { ref: internalRef, className, "data-state": isOpen ? "open" : null, children });
  }
);
ComboboxComponent.displayName = DISPLAY_NAME_COMBOBOX;
var Wrapper = forwardRef15(
  ({ as = "div", className, id, children, ...rest }, ref) => {
    return /* @__PURE__ */ jsx19(Provider, { id, ...rest, children: /* @__PURE__ */ jsx19(ComboboxComponent, { as, ref, className, children }) });
  }
);
Wrapper.displayName = DISPLAY_NAME_WRAPPER;
var Combobox = Wrapper;
Combobox.displayName = DISPLAY_NAME_COMBOBOX;
Combobox.Error = ComboboxError;
Combobox.Field = Field2;
Combobox.Input = Input2;
Combobox.Label = Label2;
Combobox.Option = Option;
Combobox.Options = Options;

// src/components/Button/Button.tsx
import { forwardRef as forwardRef16 } from "react";
import { jsx as jsx20 } from "react/jsx-runtime";
var Button = forwardRef16(
  ({ as: Element = "button", children, ...rest }, ref) => {
    const { a11yProps, htmlProps } = useButton({ as: Element });
    return /* @__PURE__ */ jsx20(Element, { ref, ...a11yProps, ...htmlProps, ...rest, children });
  }
);
Button.displayName = "Button";

// src/components/Dialog/Dialog.tsx
import {
  forwardRef as forwardRef20,
  useContext as useContext17,
  useEffect as useEffect11,
  useImperativeHandle as useImperativeHandle6,
  useRef as useRef11,
  useState as useState9
} from "react";

// src/components/Dialog/Context.tsx
import {
  Children as Children7,
  createContext as createContext3,
  isValidElement as isValidElement7,
  useContext as useContext14,
  useEffect as useEffect9,
  useMemo as useMemo10,
  useState as useState8
} from "react";
import { jsx as jsx21 } from "react/jsx-runtime";
var Context3 = createContext3({
  hasDescription: false,
  hasTitle: false,
  id: "",
  isOpen: false,
  onClose: () => {
  }
});
function Provider3({
  id: customId,
  isOpen = false,
  children,
  onClose = () => {
  },
  ...rest
}) {
  const id = useId(customId);
  const [hasTitle, setHasTitle] = useState8(false);
  const [hasDescription, setHasDescription] = useState8(false);
  const checkChild = useMemo10(
    () => (child) => {
      if (isValidElement7(child)) {
        const { displayName } = child.type;
        if (displayName === TITLE) {
          setHasTitle(true);
          return true;
        }
        if (displayName === DESCRIPTION) {
          setHasDescription(true);
          return true;
        }
        if (child.props.children) {
          Children7.forEach(child.props.children, checkChild);
        }
        return true;
      }
      return false;
    },
    []
  );
  useEffect9(() => {
    setHasDescription(false);
    setHasTitle(false);
    findChildRecursively(children, checkChild);
  }, [children, checkChild]);
  const value = useMemo10(
    () => ({
      hasDescription,
      hasTitle,
      id,
      isOpen,
      onClose
    }),
    [hasDescription, hasTitle, id, isOpen, onClose]
  );
  return /* @__PURE__ */ jsx21(Context3.Provider, { value, ...rest, children });
}
var useDialogContext = () => {
  const context = useContext14(Context3);
  if (!context) {
    throw new Error("useDialogContext must be used within a DialogProvider");
  }
  return context;
};

// src/components/Dialog/Description.tsx
import { forwardRef as forwardRef17, useContext as useContext15 } from "react";
import { jsx as jsx22 } from "react/jsx-runtime";
var Description = forwardRef17(
  ({ as: Element = "p", children, ...rest }, ref) => {
    const { id } = useContext15(Context3);
    return /* @__PURE__ */ jsx22(Element, { ref, id: `${id}-description`, ...rest, children });
  }
);
Description.displayName = DESCRIPTION;

// src/components/Dialog/Panel.tsx
import {
  forwardRef as forwardRef18,
  useEffect as useEffect10,
  useImperativeHandle as useImperativeHandle5,
  useRef as useRef10
} from "react";
import { jsx as jsx23 } from "react/jsx-runtime";
var Panel = forwardRef18(
  (props, ref) => {
    const { onClose } = useDialogContext();
    const panelRef = useRef10(null);
    useImperativeHandle5(ref, () => panelRef.current);
    useEffect10(() => {
      const handleClickOutside = (event) => {
        if (panelRef.current && !panelRef.current.contains(event.target)) {
          onClose();
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [onClose]);
    return /* @__PURE__ */ jsx23("div", { ref: panelRef, ...props });
  }
);
Panel.displayName = "Panel";

// src/components/Dialog/Title.tsx
import { forwardRef as forwardRef19, useContext as useContext16 } from "react";
import { jsx as jsx24 } from "react/jsx-runtime";
var Title = forwardRef19(
  ({ as: Element = "h3", children, ...rest }, ref) => {
    const { id } = useContext16(Context3);
    return /* @__PURE__ */ jsx24(Element, { ref, id: `${id}-title`, ...rest, children });
  }
);
Title.displayName = TITLE;

// src/components/Dialog/Dialog.tsx
import { jsx as jsx25 } from "react/jsx-runtime";
var DialogInner = forwardRef20(
  (props, ref) => {
    const { id, isOpen, hasTitle, hasDescription, onClose } = useContext17(Context3);
    const dialogRef = useRef11(null);
    const [delayedIsOpen, setDelayedIsOpen] = useState9(isOpen);
    useImperativeHandle6(ref, () => dialogRef.current);
    useEffect11(() => {
      const dialogElement = dialogRef.current;
      if (!dialogElement) {
        return;
      }
      if (isOpen) {
        dialogElement.showModal();
      } else {
        dialogElement.close();
      }
    }, [isOpen]);
    useEffect11(() => {
      const dialogElement = dialogRef.current;
      if (!dialogElement) {
        return;
      }
      const handleClose = () => onClose();
      dialogElement.addEventListener("close", handleClose);
      return () => {
        dialogElement.removeEventListener("close", handleClose);
      };
    }, [onClose]);
    useEffect11(() => {
      const timer = setTimeout(() => {
        setDelayedIsOpen(isOpen);
      }, 0);
      return () => clearTimeout(timer);
    }, [isOpen]);
    return /* @__PURE__ */ jsx25(
      "dialog",
      {
        ...props,
        ref: dialogRef,
        "aria-labelledby": hasTitle ? `${id}-title` : void 0,
        "aria-describedby": hasDescription ? `${id}-description` : void 0,
        "data-open": delayedIsOpen
      }
    );
  }
);
DialogInner.displayName = "Dialog";
var DialogComponent = forwardRef20(
  ({ isOpen, onClose = () => {
  }, id, ...rest }, ref) => {
    return /* @__PURE__ */ jsx25(Provider3, { onClose, id, isOpen, children: /* @__PURE__ */ jsx25(DialogInner, { ref, ...rest }) });
  }
);
DialogComponent.displayName = "Dialog.Provider";
var Dialog = DialogComponent;
Dialog.Description = Description;
Dialog.Panel = Panel;
Dialog.Title = Title;

// src/components/Dropdown/Dropdown.tsx
import {
  forwardRef as forwardRef24,
  useEffect as useEffect13,
  useImperativeHandle as useImperativeHandle9,
  useRef as useRef14
} from "react";
import { useOnClickOutside as useOnClickOutside2 } from "@flywire/react-hooks";

// src/components/Dropdown/Button.tsx
import {
  forwardRef as forwardRef21,
  useCallback as useCallback8
} from "react";

// src/components/Dropdown/Context.tsx
import {
  createContext as createContext4,
  useContext as useContext18,
  useMemo as useMemo11,
  useReducer as useReducer2
} from "react";

// src/components/Dropdown/constants.ts
var CLOSE_MENU = "CLOSE_MENU";
var OPEN_MENU = "OPEN_MENU";
var GO_TO_NEXT_ITEM = "GO_TO_NEXT_ITEM";
var GO_TO_PREV_ITEM = "GO_TO_PREV_ITEM";
var GO_TO_ITEM = "GO_TO_ITEM";
var REGISTER_ITEM = "REGISTER_ITEM";
var UNREGISTER_ITEM = "UNREGISTER_ITEM";
var SET_NAVIGATING_WITH_KEYBOARD = "SET_NAVIGATING_WITH_KEYBOARD";

// src/components/Dropdown/Context.tsx
import { jsx as jsx26 } from "react/jsx-runtime";
var initialState2 = {
  id: "",
  state: 0 /* Closed */,
  activeItemIndex: -1,
  items: [],
  isNavigatingWithKeyboard: true
};
var getItemId = (dropdownId, itemId) => {
  return `dropdown-${dropdownId}-item-${itemId}`;
};
var getItemsId = (dropdownId) => {
  return `dropdown-${dropdownId}-items`;
};
var getNextIndex = (currentIndex, items) => {
  return (currentIndex + 1) % items.length;
};
var getPrevIndex = (currentIndex, items) => {
  return (currentIndex - 1 + items.length) % items.length;
};
var reducer2 = (state, action) => {
  switch (action.type) {
    case OPEN_MENU: {
      if (state.state === 1 /* Open */) {
        return state;
      }
      return { ...state, state: 1 /* Open */ };
    }
    case CLOSE_MENU: {
      if (state.state === 0 /* Closed */) {
        return state;
      }
      return {
        ...state,
        state: 0 /* Closed */,
        activeItemIndex: -1,
        isNavigatingWithKeyboard: false
      };
    }
    case GO_TO_NEXT_ITEM: {
      const nextIndex = getNextIndex(state.activeItemIndex, state.items);
      return {
        ...state,
        activeItemIndex: state.activeItemIndex === -1 ? 0 : nextIndex,
        isNavigatingWithKeyboard: true
      };
    }
    case GO_TO_PREV_ITEM: {
      const prevIndex = getPrevIndex(state.activeItemIndex, state.items);
      return {
        ...state,
        activeItemIndex: state.activeItemIndex === -1 ? state.items.length - 1 : prevIndex,
        isNavigatingWithKeyboard: true
      };
    }
    case GO_TO_ITEM: {
      return { ...state, activeItemIndex: action.index };
    }
    case REGISTER_ITEM: {
      return {
        ...state,
        items: [...state.items, { id: action.id, element: action.element }]
      };
    }
    case UNREGISTER_ITEM: {
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.id)
      };
    }
    case SET_NAVIGATING_WITH_KEYBOARD: {
      if (action.value === state.isNavigatingWithKeyboard) {
        return state;
      }
      return { ...state, isNavigatingWithKeyboard: action.value };
    }
    default:
      return state;
  }
};
var Context4 = createContext4(void 0);
var Provider4 = ({
  children,
  id: customId,
  isOpen = false
}) => {
  const id = useId(customId);
  const [state, dispatch] = useReducer2(reducer2, {
    ...initialState2,
    id,
    state: isOpen ? 1 /* Open */ : 0 /* Closed */
  });
  const memoizedGetItemId = useMemo11(() => (itemId) => getItemId(id, itemId), [id]);
  const memoizedGetItemsId = useMemo11(() => () => getItemsId(id), [id]);
  return /* @__PURE__ */ jsx26(
    Context4.Provider,
    {
      value: {
        ...state,
        dispatch,
        id,
        getItemId: memoizedGetItemId,
        getItemsId: memoizedGetItemsId
      },
      children
    }
  );
};
var useDropdown = () => {
  const context = useContext18(Context4);
  if (!context) {
    throw new Error("useDropdown must be used within a Provider");
  }
  return context;
};

// src/components/Dropdown/Button.tsx
import { jsx as jsx27 } from "react/jsx-runtime";
var Button2 = forwardRef21(
  ({
    as: Element = "button",
    children,
    className,
    onClick = () => {
    },
    onKeyDown = () => {
    },
    ...rest
  }, ref) => {
    const { id, state, dispatch, items, activeItemIndex } = useDropdown();
    const handleClick = useCallback8(
      () => dispatch({ type: state === 1 /* Open */ ? CLOSE_MENU : OPEN_MENU }),
      [dispatch, state]
    );
    const handleKeydown = useCallback8(
      (evt) => {
        switch (evt.key) {
          case ENTER:
          case SPACE: {
            evt.preventDefault();
            if (items[activeItemIndex]) {
              const activeItem = items[activeItemIndex].element;
              activeItem?.click();
            }
            dispatch({ type: state === 0 /* Closed */ ? OPEN_MENU : CLOSE_MENU });
            break;
          }
          case ARROW_DOWN: {
            evt.preventDefault();
            dispatch({ type: OPEN_MENU });
            dispatch({ type: GO_TO_NEXT_ITEM });
            break;
          }
          case ARROW_UP: {
            evt.preventDefault();
            dispatch({ type: OPEN_MENU });
            setTimeout(() => dispatch({ type: GO_TO_PREV_ITEM }), 0);
            break;
          }
          case ESCAPE: {
            evt.preventDefault();
            dispatch({ type: CLOSE_MENU });
            break;
          }
          default:
            break;
        }
      },
      [dispatch, state, activeItemIndex, items]
    );
    return /* @__PURE__ */ jsx27(
      Element,
      {
        ref,
        className,
        "aria-expanded": state === 1 /* Open */,
        "aria-controls": `dropdown-${id}-items`,
        "data-open": state === 1 /* Open */,
        id: `dropdown-${id}-button`,
        "aria-haspopup": true,
        onClick: callAll(onClick, handleClick),
        onKeyDown: callAll(onKeyDown, handleKeydown),
        ...rest,
        children
      }
    );
  }
);
Button2.displayName = "Button";

// src/components/Dropdown/Item.tsx
import {
  forwardRef as forwardRef22,
  useEffect as useEffect12,
  useImperativeHandle as useImperativeHandle7,
  useRef as useRef12
} from "react";
import { jsx as jsx28 } from "react/jsx-runtime";
var Item = forwardRef22(
  ({
    as: Element = "li",
    children,
    className,
    disabled = false,
    onClick = () => {
    },
    render,
    ...rest
  }, ref) => {
    const { dispatch, getItemId: getItemId2, items, activeItemIndex, isNavigatingWithKeyboard } = useDropdown();
    const uniqId = useId();
    const itemId = getItemId2(uniqId);
    const internalRef = useRef12(null);
    const isFocused = activeItemIndex !== null && items[activeItemIndex]?.id === itemId;
    const close = () => {
      dispatch({ type: CLOSE_MENU });
    };
    useImperativeHandle7(ref, () => internalRef.current);
    useEffect12(() => {
      if (!disabled) {
        dispatch({ type: REGISTER_ITEM, id: itemId, element: internalRef.current });
      }
      return () => {
        dispatch({ type: UNREGISTER_ITEM, id: itemId });
      };
    }, [disabled, dispatch, itemId]);
    const handleClick = (evt) => {
      if (disabled) {
        evt.preventDefault();
        return;
      }
      close();
    };
    return /* @__PURE__ */ jsx28(
      Element,
      {
        "aria-disabled": disabled,
        className,
        "data-disabled": disabled,
        "data-focus": isFocused,
        "data-pointer-disabled": isNavigatingWithKeyboard,
        id: itemId,
        onClick: callAll(onClick, handleClick),
        ref: internalRef,
        role: "menuitem",
        tabIndex: -1,
        ...rest,
        children: render ? render({ close, isFocused }) : children
      }
    );
  }
);
Item.displayName = "Item";

// src/components/Dropdown/Items.tsx
import {
  forwardRef as forwardRef23,
  useImperativeHandle as useImperativeHandle8,
  useRef as useRef13
} from "react";
import { jsx as jsx29 } from "react/jsx-runtime";
var Items = forwardRef23(
  ({
    as: Element = "ul",
    children,
    "data-position": dataPosition = "bottom-end",
    onMouseMove = () => {
    },
    className,
    ...rest
  }, ref) => {
    const { activeItemIndex, getItemsId: getItemsId2, id, state, dispatch, items } = useDropdown();
    const internalRef = useRef13(null);
    const itemsId = getItemsId2();
    useImperativeHandle8(ref, () => internalRef.current, [internalRef]);
    const handleMouseMove = () => {
      dispatch({ type: SET_NAVIGATING_WITH_KEYBOARD, value: false });
    };
    return /* @__PURE__ */ jsx29(
      Element,
      {
        "aria-labelledby": `dropdown-${id}-button`,
        "aria-activedescendant": activeItemIndex >= 0 ? items[activeItemIndex]?.id : void 0,
        className,
        "data-open": state === 1 /* Open */,
        "data-position": dataPosition,
        id: itemsId,
        ref: internalRef,
        role: "menu",
        tabIndex: -1,
        onMouseMove: callAll(onMouseMove, handleMouseMove),
        hidden: state === 0 /* Closed */,
        ...rest,
        children: state === 1 /* Open */ && children
      }
    );
  }
);
Items.displayName = "Items";

// src/components/Dropdown/Dropdown.tsx
import { jsx as jsx30 } from "react/jsx-runtime";
var DropdownComponent = forwardRef24(
  ({ as: Element = "div", children, className, render, onChange = () => {
  }, ...rest }, ref) => {
    const internalRef = useRef14(null);
    const { state, dispatch } = useDropdown();
    const isOpen = state === 1 /* Open */;
    useImperativeHandle9(ref, () => internalRef.current, [internalRef]);
    useOnClickOutside2(internalRef, () => {
      if (isOpen) onChange?.(false);
      dispatch({ type: CLOSE_MENU });
    });
    useEffect13(() => {
      onChange?.(isOpen);
    }, [isOpen, onChange]);
    return /* @__PURE__ */ jsx30(Element, { "data-open": isOpen, ref: internalRef, className, ...rest, children: render ? render({ isOpen }) : children });
  }
);
DropdownComponent.displayName = "Dropdown";
var Wrapper2 = forwardRef24(
  ({ as = "div", className, children, onChange = () => {
  }, ...rest }, ref) => {
    return /* @__PURE__ */ jsx30(Provider4, { ...rest, children: /* @__PURE__ */ jsx30(DropdownComponent, { as, ref, className, onChange, children }) });
  }
);
Wrapper2.displayName = "Dropdown";
var Dropdown = Wrapper2;
Dropdown.Button = Button2;
Dropdown.Item = Item;
Dropdown.Items = Items;

// src/components/Tooltip/Tooltip.tsx
import {
  forwardRef as forwardRef27,
  useCallback as useCallback9,
  useContext as useContext20,
  useEffect as useEffect14,
  useImperativeHandle as useImperativeHandle10,
  useRef as useRef15
} from "react";
import { useOnClickOutside as useOnClickOutside3 } from "@flywire/react-hooks";

// src/components/Tooltip/Content.tsx
import { forwardRef as forwardRef25 } from "react";

// src/components/Tooltip/Context.tsx
import {
  createContext as createContext5,
  useContext as useContext19,
  useMemo as useMemo12,
  useState as useState10
} from "react";
import { jsx as jsx31 } from "react/jsx-runtime";
var Context5 = createContext5({
  id: "",
  isOpen: false,
  onChange: () => {
  },
  position: "top",
  setIsOpen: () => {
  }
});
function Provider5({
  id: customId,
  isOpen: defaultIsOpen = false,
  children,
  onChange = () => {
  },
  position = "top",
  ...rest
}) {
  const id = useId(customId);
  const [isOpen, setIsOpen] = useState10(defaultIsOpen);
  const value = useMemo12(
    () => ({
      id,
      isOpen,
      setIsOpen,
      onChange,
      position
    }),
    [id, isOpen, onChange, position]
  );
  return /* @__PURE__ */ jsx31(Context5.Provider, { value, ...rest, children });
}
var useTooltipContext = () => {
  const context = useContext19(Context5);
  if (!context) {
    throw new Error("useTooltipContext must be used within a TooltipProvider");
  }
  return context;
};

// src/components/Tooltip/Content.tsx
import { jsx as jsx32 } from "react/jsx-runtime";
var Content = forwardRef25(
  ({ as: Element = "div", className, children, render, ...rest }, ref) => {
    const { id, isOpen, setIsOpen, position } = useTooltipContext();
    return /* @__PURE__ */ jsx32(
      Element,
      {
        className: typeof className === "function" ? className({ isOpen }) : className,
        "data-open": isOpen,
        "data-position": position,
        hidden: !isOpen,
        id: `${id}-content`,
        ref,
        role: "tooltip",
        ...rest,
        children: render ? render({ isOpen, close: () => setIsOpen(false) }) : children
      }
    );
  }
);
Content.displayName = "Content";

// src/components/Tooltip/Trigger.tsx
import {
  forwardRef as forwardRef26
} from "react";
import { jsx as jsx33 } from "react/jsx-runtime";
var Trigger = forwardRef26(
  ({
    as: Element = "button",
    className,
    children,
    render,
    onClick = () => {
    },
    onKeyDown = () => {
    },
    onFocus = () => {
    },
    onBlur = () => {
    },
    onMouseEnter = () => {
    },
    onMouseLeave = () => {
    },
    ...rest
  }, ref) => {
    const { id, isOpen, setIsOpen } = useTooltipContext();
    const handleClick = (evt) => {
      evt.preventDefault();
      setIsOpen((isOpen2) => !isOpen2);
    };
    const handleFocus = () => setIsOpen(true);
    const handleBlur = () => setIsOpen(false);
    const handleKeyDown = (evt) => {
      if (evt.key === SPACE || evt.key === ENTER) {
        evt.preventDefault();
        setIsOpen((isOpen2) => !isOpen2);
      }
    };
    return /* @__PURE__ */ jsx33(
      Element,
      {
        "aria-describedby": `${id}-content`,
        className: typeof className === "function" ? className({ isOpen }) : className,
        onBlur: callAll(onBlur, handleBlur),
        onClick: callAll(onClick, handleClick),
        onFocus: callAll(onFocus, handleFocus),
        onKeyDown: callAll(onKeyDown, handleKeyDown),
        onMouseEnter: callAll(onMouseEnter, handleFocus),
        onMouseLeave: callAll(onMouseLeave, handleBlur),
        ref,
        tabIndex: 0,
        ...rest,
        children: render ? render({ isOpen }) : children
      }
    );
  }
);
Trigger.displayName = "Trigger";

// src/components/Tooltip/Tooltip.tsx
import { jsx as jsx34 } from "react/jsx-runtime";
var TooltipComponent = forwardRef27(
  ({ as: Element = "div", children, className }, forwardedRef) => {
    const internalRef = useRef15(null);
    const { id, isOpen, setIsOpen } = useContext20(Context5);
    useImperativeHandle10(forwardedRef, () => internalRef.current);
    useOnClickOutside3(
      internalRef,
      useCallback9(() => setIsOpen(false), [setIsOpen])
    );
    useEffect14(() => {
      const handleKeyDown = (event) => {
        if (event.key === ESCAPE && isOpen) {
          setIsOpen(false);
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }, [isOpen, setIsOpen]);
    return /* @__PURE__ */ jsx34(
      Element,
      {
        ref: internalRef,
        className,
        "data-state": isOpen ? "open" : null,
        role: "tooltip",
        id,
        children
      }
    );
  }
);
TooltipComponent.displayName = "Tooltip";
var Wrapper3 = forwardRef27(
  ({ as = "div", className, id, children, position = "top", ...rest }, ref) => {
    return /* @__PURE__ */ jsx34(Provider5, { id, position, ...rest, children: /* @__PURE__ */ jsx34(TooltipComponent, { as, ref, className, children }) });
  }
);
Wrapper3.displayName = "Tooltip";
var Tooltip = Wrapper3;
Tooltip.displayName = "Tooltip";
Tooltip.Trigger = Trigger;
Tooltip.Content = Content;
export {
  Button,
  Combobox,
  Dialog,
  Dropdown,
  Field,
  Tooltip,
  useAutoPosition,
  useButton,
  useDatePicker,
  useId,
  useInput,
  useMaskInput,
  useMoneyInput,
  useRunAfterUpdate
};
//# sourceMappingURL=index.js.map
